/**
 * MKS-344 — the Contacts list under Sales.
 *
 * Deliberately minimal, mirroring the model: search (name, e-mail, company),
 * a company filter, inline edit, and a delete that is a soft delete. Each row
 * names the leads the contact is on, which is what makes "is it safe to
 * delete this?" answerable here instead of by opening every lead.
 */
import { useState } from 'react';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { useContacts, useCreateContact, useUpdateContact, useDeleteContact } from '../../hooks/useContacts';
import { useCustomers } from '../../hooks/useProjects';
import type { Contact, ContactInput } from '../../services/api/contacts';

const EMPTY_FORM: ContactInput = { name: '', customerId: null, email: null, phone: null };

export default function ContactsPage() {
  const [search, setSearch] = useState('');
  const [customerId, setCustomerId] = useState<number | null>(null);
  // `null` = closed, `'new'` = the create row, a number = editing that contact.
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const [form, setForm] = useState<ContactInput>(EMPTY_FORM);

  const { data, isLoading } = useContacts({ search, customerId });
  const { data: customers } = useCustomers();
  const createContact = useCreateContact();
  const updateContact = useUpdateContact();
  const deleteContact = useDeleteContact();

  const contacts = data?.data ?? [];
  const allCustomers = customers ?? [];

  const startCreate = () => {
    setForm(EMPTY_FORM);
    setEditing('new');
  };

  const startEdit = (contact: Contact) => {
    setForm({
      name: contact.name,
      customerId: contact.customerId,
      email: contact.email,
      phone: contact.phone,
    });
    setEditing(contact.id);
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const save = () => {
    if (!form.name.trim()) return;
    const input: ContactInput = {
      name: form.name.trim(),
      customerId: form.customerId ?? null,
      email: form.email?.trim() || null,
      phone: form.phone?.trim() || null,
    };
    if (editing === 'new') {
      createContact.mutate(input, { onSuccess: cancelEdit });
    } else if (typeof editing === 'number') {
      updateContact.mutate({ id: editing, input }, { onSuccess: cancelEdit });
    }
  };

  const remove = (contact: Contact) => {
    const used = contact.leads.length;
    const message = used
      ? `Delete ${contact.name}? It stays on ${used} lead${used === 1 ? '' : 's'} but disappears from the pickers.`
      : `Delete ${contact.name}?`;
    if (!window.confirm(message)) return;
    deleteContact.mutate(contact.id);
  };

  const editor = (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-5 items-end rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="sm:col-span-1">
        <label htmlFor="contact-name" className="block text-xs font-medium text-gray-600 mb-1">
          Name *
        </label>
        <input
          id="contact-name"
          type="text"
          value={form.name}
          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      </div>
      <div className="sm:col-span-1">
        <label htmlFor="contact-company" className="block text-xs font-medium text-gray-600 mb-1">
          Company
        </label>
        <select
          id="contact-company"
          value={form.customerId == null ? '' : String(form.customerId)}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              customerId: e.target.value ? parseInt(e.target.value, 10) : null,
            }))
          }
          className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">—</option>
          {allCustomers.map((c) => (
            <option key={c.id} value={String(c.id)}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      <div className="sm:col-span-1">
        <label htmlFor="contact-email" className="block text-xs font-medium text-gray-600 mb-1">
          E-mail
        </label>
        <input
          id="contact-email"
          type="email"
          value={form.email ?? ''}
          onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
          className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      </div>
      <div className="sm:col-span-1">
        <label htmlFor="contact-phone" className="block text-xs font-medium text-gray-600 mb-1">
          Phone
        </label>
        <input
          id="contact-phone"
          type="tel"
          value={form.phone ?? ''}
          onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
          className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      </div>
      <div className="flex gap-2 sm:col-span-1">
        <Button onClick={save} disabled={!form.name.trim()}>
          Save
        </Button>
        <button
          type="button"
          onClick={cancelEdit}
          className="text-[13px] font-medium text-gray-500 hover:text-gray-700"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Contacts</h1>
        <Button onClick={startCreate}>+ New contact</Button>
      </div>

      <div className="mb-4 flex flex-wrap gap-3">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, e-mail or company..."
          aria-label="Search contacts"
          className="w-64 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <select
          value={customerId == null ? '' : String(customerId)}
          onChange={(e) => setCustomerId(e.target.value ? parseInt(e.target.value, 10) : null)}
          aria-label="Filter by company"
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">All companies</option>
          {allCustomers.map((c) => (
            <option key={c.id} value={String(c.id)}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {editing === 'new' && <div className="mb-4">{editor}</div>}

      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner />
        </div>
      ) : contacts.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 py-12 text-gray-400">
          <p className="mb-1 text-[13px]">No contacts found.</p>
          <p className="text-[13px]">A contact is a person, a company, an e-mail and a phone — nothing else.</p>
        </div>
      ) : (
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-500">
              <th className="py-2 pr-4">Name</th>
              <th className="py-2 pr-4">Company</th>
              <th className="py-2 pr-4">E-mail</th>
              <th className="py-2 pr-4">Phone</th>
              <th className="py-2 pr-4">Leads</th>
              <th className="py-2 pr-4" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {contacts.map((contact) =>
              editing === contact.id ? (
                <tr key={contact.id}>
                  <td colSpan={6} className="py-3">
                    {editor}
                  </td>
                </tr>
              ) : (
                <tr key={contact.id}>
                  <td className="py-3 pr-4 text-[13px] font-medium text-gray-900">{contact.name}</td>
                  <td className="py-3 pr-4 text-[13px] text-gray-500">
                    {contact.customer?.name ?? '—'}
                  </td>
                  <td className="py-3 pr-4 text-[13px]">
                    {contact.email ? (
                      <a
                        href={`mailto:${contact.email}`}
                        className="text-primary-600 hover:underline"
                      >
                        {contact.email}
                      </a>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="py-3 pr-4 text-[13px]">
                    {contact.phone ? (
                      <a href={`tel:${contact.phone}`} className="text-primary-600 hover:underline">
                        {contact.phone}
                      </a>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="py-3 pr-4 text-[13px] text-gray-500">
                    {/* Names, not links: the board has no deep link to a
                        single lead, so a link here would only reload the
                        board. The name plus its stage is what someone needs
                        to decide whether this contact is still in play. */}
                    {contact.leads.length === 0 ? (
                      <span className="text-gray-400">—</span>
                    ) : (
                      <span className="flex flex-wrap gap-x-2">
                        {contact.leads.map((lead) => (
                          <span key={lead.id} title={`Stage: ${lead.stage}`}>
                            {lead.name}
                            <span className="text-gray-400"> ({lead.stage})</span>
                          </span>
                        ))}
                      </span>
                    )}
                  </td>
                  <td className="py-3 pr-4">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => startEdit(contact)}
                        aria-label={`Edit ${contact.name}`}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(contact)}
                        aria-label={`Delete ${contact.name}`}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
